import React from 'react'
import { Direction, fallbackThemeValues, KeyCode, KeyName, LocalStorageItem, SessionStorageItem } from '../constants'
import classNames from 'classnames'
import { ImageSize } from '@enums/images.enum'
import { ActorsViewType } from '@enums/details.enum'
import { SearchData } from '@interfaces/index'
import { DocumentData } from 'firebase/firestore'
import { CardsNumber } from '@enums/cards.enum'
import { ProfileImagesData, ProfileImagesType } from 'sdk/services/config/types/config'
import { ProfileIconType } from '@enums/profile.enum'
import { defaultProfileImage, UserProfile } from 'sdk/services/storage/profiles/types/profilesManager'
import { storageProfileIdentifier } from '@constants/profile.const'

export const onEnterKeyPress = (e: React.KeyboardEvent, onClick: () => void) => {
  if (e.key === KeyName.Enter || e.keyCode === KeyCode.Enter) {
    onClick()
  }
}

export const onBackKeyPress = (e: KeyboardEvent, onClick: () => void) => {
  if (e.key === KeyName.Escape || e.keyCode === KeyCode.TizenBackKey || e.keyCode === KeyCode.WebosBackKey) {
    onClick()
  }
}

export const onBackKeyPressAndClick = (e: React.KeyboardEvent | React.MouseEvent, onClick: () => void) => {
  const keyboardEvent = e as React.KeyboardEvent
  if (
    e.type === 'click' ||
    (e.type === 'keydown' &&
      (keyboardEvent.key === KeyName.Escape ||
        keyboardEvent.keyCode === KeyCode.TizenBackKey ||
        keyboardEvent.keyCode === KeyCode.WebosBackKey))
  ) {
    onClick()
  }
}

export const onEnterKeyPressAndClick = (e: React.KeyboardEvent | React.MouseEvent, onClick: () => void) => {
  if (
    e.type === 'click' ||
    (e.type === 'keydown' &&
      (e as React.KeyboardEvent).key === KeyName.Enter &&
      (e as React.KeyboardEvent).keyCode === 13)
  ) {
    onClick()
  }
}

export const createAlt = (imageUrl: string): string => imageUrl?.split('/')?.pop()?.replace('.', '') || ''

export function flatten<T>(arr: T[][]): T[] {
  return ([] as T[]).concat(...arr)
}

export const openMenuOnBackKey = (e: KeyboardEvent) => {
  onBackKeyPress(e, () => {
    const menu = document.getElementById('menu')
    const activeMenuElement = menu?.querySelector('.active') as HTMLElement
    activeMenuElement?.focus()
  })
}

export const formatSecondsToHoursAndMinutes = (seconds: number) => {
  const totalSeconds = Math.floor(seconds)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)

  const formattedHours = hours < 10 ? `${hours}` : hours.toString()
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes.toString()

  return `${formattedHours}h ${formattedMinutes}m`
}

export const remainingTimeForWatchingFormatter = (seconds: number) => {
  const totalMinutes = Math.floor(seconds / 60)

  if (totalMinutes < 1) {
    return 'less than 1 min remaining'
  }

  if (totalMinutes < 60) {
    return `${totalMinutes} min remaining`
  }

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const formattedHours = `${hours}h`
  const formattedMinutes = minutes > 0 ? ` ${minutes} min remaining` : ''

  return `${formattedHours}${formattedMinutes}`
}

//Determines if the "move down" action should be disabled for an item
//based on its position in the array and the group size (suitable for MyList
//and Search items)
export const disableMoveDown = (
  index: number,
  array: DocumentData[] | SearchData[],
  groupSize: CardsNumber,
): '' | undefined => {
  const remainder = array.length % groupSize
  const finalGroupSize = remainder === 0 ? groupSize : remainder

  return index >= array.length - finalGroupSize ? '' : undefined
}

export const timeStringToSeconds = (timeString: string): number => {
  if (timeString) {
    const [hours, minutes, seconds] = timeString.split(':').map(Number)
    return hours * 3600 + minutes * 60 + seconds
  }
  return 0
}

export const convertDate = (dateProp: string) => {
  const splitedDate = dateProp.split('-')
  const month = splitedDate[1]
  const day = splitedDate[2]
  const dayLastCharacter = day.slice(-1)

  let daySuffix
  let monthName

  switch (dayLastCharacter) {
    case '1':
      daySuffix = 'st'
      break

    case '2':
      daySuffix = 'nd'
      break

    case '3':
      daySuffix = 'rd'
      break

    default:
      daySuffix = 'th'
      break
  }

  switch (month) {
    case '01':
      monthName = 'January'
      break
    case '02':
      monthName = 'February'
      break
    case '03':
      monthName = 'March'
      break
    case '04':
      monthName = 'April'
      break
    case '05':
      monthName = 'May'
      break
    case '06':
      monthName = 'June'
      break
    case '07':
      monthName = 'July'
      break
    case '08':
      monthName = 'August'
      break
    case '09':
      monthName = 'September'
      break
    case '10':
      monthName = 'October'
      break
    case '11':
      monthName = 'November'
      break
    case '12':
      monthName = 'December'
      break
  }

  return `${day}${daySuffix} ${monthName}`
}

export const ageRateColorClass = (age: string | undefined) => {
  if (age === '+16') {
    return 'ageRating16'
  } else if (age === '+12') {
    return 'ageRating12'
  } else if (age === '+7') {
    return 'ageRating7'
  } else if (age === '+0') {
    return 'ageRating0'
  }
}

export const convertHexToRgb = (color: string) => {
  const hexValue = color?.replace('#', '')
  const r = parseInt(hexValue?.substring(0, 2), 16)
  const g = parseInt(hexValue?.substring(2, 4), 16)
  const b = parseInt(hexValue?.substring(4, 6), 16)
  return `${r}, ${g}, ${b}`
}

export const emailLoginPattern =
  /* eslint-disable-next-line */
  /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i

export const formatTime = (time: number) => {
  const hours = Math.floor(time / 3600)
  const minutes = Math.floor((time % 3600) / 60)
  const seconds = Math.floor(time % 60)

  return `${hours}:${formatDigit(minutes)}:${formatDigit(seconds)}`
}

const formatDigit = (num: number) => {
  return (num < 10 ? '0' : '') + num
}

export const addLastFocusedItemToSS = (id: string, page: string) => {
  sessionStorage.setItem(`lastFocusedCard-${page}`, JSON.stringify(id))
}

export const addRailItemToSS = (
  railId: string,
  cardTemplate: string,
  cardId: string,
  cardIndex: number,
  homeSlug: string,
  heroFocusOn?: string,
) => {
  sessionStorage.setItem(
    SessionStorageItem.LAST_FOCUSED_ITEM_HOME,
    JSON.stringify({
      railId: railId,
      cardTemplate: cardTemplate,
      cardId: cardId,
      cardIndex: cardIndex,
      homeSlug: homeSlug,
      heroFocusOn: heroFocusOn,
    }),
  )
}

export const moveInputCursor = (direction: Direction, element: HTMLInputElement | null) => {
  let cursorPoint = element?.value.length
  switch (direction) {
    case 'left':
      if (element?.selectionEnd) {
        cursorPoint = element?.selectionEnd - 1
        element?.setSelectionRange(element.value.length, cursorPoint, 'backward')
      }
      break
    case 'right':
      if (element?.selectionEnd || element?.selectionEnd === 0) {
        cursorPoint = element?.selectionEnd + 1
        element?.setSelectionRange(element.value.length, cursorPoint, 'forward')
      }
      break
    default:
      element?.setSelectionRange(element?.selectionStart, element?.selectionEnd)
      break
  }
}

export const addSynopsisClass = (value: string, conditions: boolean[]) => {
  const fullWidth = conditions.some((v) => v)
  const addMargin = conditions.filter((v) => v).length === 1
  return classNames(`synopsis-${value}`, { 'synopsis-full-width': fullWidth, 'synopsis-gap': addMargin })
}

export const actorImageSize = (actorViewTypeClass: ActorsViewType | string) => {
  switch (actorViewTypeClass) {
    case ActorsViewType.RECTANGULAR:
      return ImageSize.ACTOR_RECTANGULAR
    case ActorsViewType.ROUNDED:
      return ImageSize.ACTOR_ROUNDED
    case ActorsViewType.SQUARED:
      return ImageSize.ACTOR_SQUARED
    default:
      return ActorsViewType.SQUARED
  }
}

export const exitTizenApplication = () => {
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    // eslint-disable-next-line no-undef
    const tizenApplication = tizen.application.getCurrentApplication()
    tizenApplication.exit()
  } catch {
    console.warn('This is not a Tizen application')
  }
}

export const exitWebOSApplication = () => {
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    // eslint-disable-next-line no-undef
    webOS.platformBack()
  } catch {
    console.warn('This is not a WebOS application')
  }
}

export const fallbackTheme = () => {
  fallbackThemeValues.map((color) => document.documentElement.style.setProperty(color.name, color.value))
}

export const getErrorCode = (errorCode: string, HTTPcode: string, versionNumber: string) => {
  return `VEL8.${errorCode}.${HTTPcode}V${versionNumber}`
}

export const getUserLanguageValue = (vocab: { [key: string]: string }, userLang: string): string => {
  const languageMap: { [key: string]: keyof typeof vocab } = {
    en: 'vocabularyLanguage_en',
    es: 'vocabularyLanguage_es',
    fr: 'vocabularyLanguage_fr',
    de: 'vocabularyLanguage_de',
    it: 'vocabularyLanguage_it',
    pt: 'vocabularyLanguage_pt',
    ru: 'vocabularyLanguage_ru',
    zh: 'vocabularyLanguage_zh',
    ja: 'vocabularyLanguage_ja',
    ar: 'vocabularyLanguage_ar',
    nl: 'vocabularyLanguage_nl',
    pl: 'vocabularyLanguage_pl',
    sv: 'vocabularyLanguage_sv',
    da: 'vocabularyLanguage_da',
    fi: 'vocabularyLanguage_fi',
    no: 'vocabularyLanguage_no',
    cs: 'vocabularyLanguage_cs',
    sk: 'vocabularyLanguage_sk',
    sl: 'vocabularyLanguage_sl',
    hu: 'vocabularyLanguage_hu',
    ro: 'vocabularyLanguage_ro',
    bg: 'vocabularyLanguage_bg',
    el: 'vocabularyLanguage_el',
    hr: 'vocabularyLanguage_hr',
    sr: 'vocabularyLanguage_sr',
    lt: 'vocabularyLanguage_lt',
    lv: 'vocabularyLanguage_lv',
    et: 'vocabularyLanguage_et',
    uk: 'vocabularyLanguage_uk',
    ga: 'vocabularyLanguage_ga',
    mt: 'vocabularyLanguage_mt',
    is: 'vocabularyLanguage_is',
    bs: 'vocabularyLanguage_bs',
    mk: 'vocabularyLanguage_mk',
    al: 'vocabularyLanguage_al',
  }

  const vocabKey = (userLang && languageMap[userLang]) || 'vocabularyLanguage_en'
  return vocab[vocabKey]
}

export const getLetterImageTypeData = (profileImages: ProfileImagesType[]) => {
  const letterImageData = profileImages?.filter((images) =>
    images.data.find((data) => data.type === ProfileIconType.LETTER),
  )?.[0]?.data
  return letterImageData ?? defaultProfileImage
}

export const isClassicsImage = (profile: UserProfile) => profile?.image?.slug?.includes('classics-')

/**
 * Type guard to check if profileData is UserProfile
 * Checks for the 'identifier' property which is unique to UserProfile
 */
export const isUserProfile = (data: UserProfile | ProfileImagesData | undefined | null): data is UserProfile => {
  return data !== undefined && data !== null && storageProfileIdentifier in data
}

export const kidsTagColor = (profileData: UserProfile | ProfileImagesData) => {
  const imageData = isUserProfile(profileData) ? profileData.image : profileData
  return imageData && imageData.type === ProfileIconType.IMAGE ? imageData.backgroundColor : imageData?.color
}

export const insertVariableOnString = (text: string, values: Record<string, string | number>): string => {
  return text?.replace(/%{([^}]+)}/g, (match, valueName: string) => {
    return values[valueName] ? String(values[valueName]) : match
  })
}

export const getActiveProfile = (): UserProfile | undefined => {
  try {
    const data = localStorage.getItem(LocalStorageItem.ACTIVE_PROFILE)
    return data ? JSON.parse(data) : undefined
  } catch {
    return undefined
  }
}
